import React from 'react'
import { CVRParticipant } from '../../types'
import { Badge, CountryFlagIcon, FlagChip } from '../ui'
import EntityProfileLink from '../watchlist/EntityProfileLink'
import WatchlistIndicator from '../watchlist/WatchlistIndicator'
import { participantWatchlistEntity } from '../../utils/watchlistEntity'
import {
  formatAddressCountryLabel,
  formatDate,
  formatLegalRegisterPercent,
  formatOwnershipStakeLabels,
  formatParticipantStripPeriod,
  formatPercentRange,
  formatProtectedAddressNote,
  participantCountryFlagCode,
} from '../../utils/format'
import { roleBadgeLabel } from '../../utils/participantRole'
import CompanyRenamedLine from './CompanyRenamedLine'

/**
 * One-row representation of a participant (person or company) in any of
 * the profile-page sections. Designed to read like an editorial row:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Name [foreign/protected/historic flags] [role badge]        │
 *   │ Address line (muted)                                        │
 *   │ Period (muted)                       Capital %   Voting %   │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * The variant controls which auxiliary info is shown:
 *   - "owner":   capital + voting %, plus from/to dates.
 *   - "role":    role/function label + from/to dates (no %).
 *   - "compact": just name + flags, used when the section is collapsed.
 *   - "strip":   one-line overview — name, flags, capital % only (no address/dates).
 */
export type ParticipantRowVariant = 'owner' | 'role' | 'compact' | 'strip'

interface ParticipantRowProps {
  participant: CVRParticipant
  variant?: ParticipantRowVariant
  /** Force-render the historic flag. By default we infer it from `role.isActive`. */
  forceHistoric?: boolean
  /** Expanded historic/sub-list inside ownership strip — subdued row styling. */
  stripExpanded?: boolean
  /** Strip row only — omit outer `<li>` when nested inside a parent list item. */
  stripInline?: boolean
}

const StripExpandLink: React.FC<{
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ expanded, onToggle, children }) => (
  <button
    type="button"
    onClick={onToggle}
    className="text-[13px] font-semibold text-ink-600 hover:text-ink underline decoration-ink-300 underline-offset-[3px] whitespace-nowrap"
    aria-expanded={expanded}
  >
    {children}
  </button>
)

const StripSectionHeader: React.FC<{
  title: string
  suffix?: string | null
  action?: React.ReactNode
}> = ({ title, suffix, action }) => (
  <div className="flex items-start justify-between gap-x-2 gap-y-1 mb-2 min-w-0">
    <p className="text-[13px] font-semibold text-ink-500 leading-snug min-w-0">
      {title}
      {suffix ? (
        <span className="font-normal text-ink-400 ml-1">
          · {suffix}
        </span>
      ) : null}
    </p>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
)

const StripHistoricDivider: React.FC = () => (
  <hr className="border-0 border-t border-rule-strong my-1.5" aria-hidden />
)

export { StripExpandLink, StripHistoricDivider, StripSectionHeader }

const ParticipantRow: React.FC<ParticipantRowProps> = ({
  participant,
  variant = 'owner',
  forceHistoric = false,
  stripExpanded = false,
  stripInline = false,
}) => {
  const isHistoric = forceHistoric || !participant.role.isActive
  const flagCode = participantCountryFlagCode(participant)
  const isProtected = participant.isAddressConfidential

  const name = participant.navn || 'Ukendt'
  const isStrip = variant === 'strip'
  const stripSubdued = isStrip && stripExpanded
  const nameClass = isStrip
    ? `text-[14.5px] truncate ${stripSubdued ? 'font-normal' : 'font-semibold'} ${isHistoric ? 'text-ink-500' : 'text-ink'}`
    : `${isHistoric ? 'text-ink-500' : 'text-ink'}`
  const isRole = variant === 'role'
  const roleLabel = isRole ? roleBadgeLabel(participant.role) : null
  const showRenamedCompany =
    participant.enhedstype === 'VIRKSOMHED' && !!participant.navnNu
  const stripPercent = isStrip ? formatStripPercent(participant) : null
  const stripPeriod = stripSubdued ? formatParticipantStripPeriod(participant.role) : null
  const watchlistEntity = isStrip ? participantWatchlistEntity(participant) : null

  if (isStrip) {
    const stripBody = (
      <>
        <div className="flex items-baseline justify-between gap-2 min-w-0">
          <div className="flex items-baseline gap-1 min-w-0 flex-1">
            <NameLink
              participant={participant}
              showWatchlist={false}
              className={`${nameClass} min-w-0`}
            >
              {name}
            </NameLink>
            {flagCode && (
              <CountryFlagIcon code={flagCode} />
            )}
            {watchlistEntity && (
              <WatchlistIndicator
                entityType={watchlistEntity.entityType}
                entityId={watchlistEntity.entityId}
              />
            )}
            {isHistoric && !stripExpanded && <FlagChip kind="historic" />}
          </div>
          {stripPercent && (
            <span
              className="text-[13px] tabular-nums text-ink-500 shrink-0"
              title={stripPercent.full}
            >
              {stripPercent.compact}
            </span>
          )}
        </div>
        {stripPeriod && (
          <p className="text-xs text-ink-400 tabular-nums mt-0.5">{stripPeriod}</p>
        )}
        {showRenamedCompany && (
          <CompanyRenamedLine
            navnNu={participant.navnNu!}
            cvrNummer={participant.cvrNummer}
            dense
          />
        )}
      </>
    )

    if (stripInline) {
      return <div className="min-w-0">{stripBody}</div>
    }

    return <li className="py-1">{stripBody}</li>
  }

  return (
    <li className="py-2 border-b border-rule last:border-b-0">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-[16rem] flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <NameLink participant={participant} className={nameClass}>
              {name}
            </NameLink>
            {flagCode && (
              <CountryFlagIcon code={flagCode} />
            )}
            {!isRole && isHistoric && <FlagChip kind="historic" />}
            {roleLabel && (
              <Badge tone={isHistoric ? 'muted' : 'neutral'} title="Rolle">
                {roleLabel}
              </Badge>
            )}
          </div>

          {showRenamedCompany && (
            <CompanyRenamedLine
              navnNu={participant.navnNu!}
              cvrNummer={participant.cvrNummer}
            />
          )}

          {variant !== 'compact' && (
            <p className="text-xs text-ink-500 mt-0.5">
              {isProtected ? (
                formatProtectedAddressNote(participant.adresse)
              ) : participant.adresse ? (
                formatAddressCountryLabel(participant.adresse)
              ) : (
                <em className="text-ink-400">Adresse ikke oplyst</em>
              )}
            </p>
          )}
        </div>

        {variant === 'owner' && (
          <OwnerStakes participant={participant} />
        )}

        {variant === 'role' && (
          <RolePeriod participant={participant} />
        )}
      </div>
    </li>
  )
}

const NameLink: React.FC<{
  participant: CVRParticipant
  className?: string
  showWatchlist?: boolean
  children: React.ReactNode
}> = ({ participant, className = '', showWatchlist = true, children }) => {
  const entity = participantWatchlistEntity(participant)
  if (!participant.enhedsNummer || !participant.navn) {
    return (
      <span className={`inline-flex items-baseline gap-1 ${className}`}>
        <span>{children}</span>
        {showWatchlist && entity && (
          <WatchlistIndicator
            entityType={entity.entityType}
            entityId={entity.entityId}
          />
        )}
      </span>
    )
  }
  if (participant.enhedstype === 'VIRKSOMHED') {
    if (!participant.cvrNummer) {
      return (
        <span className={`inline-flex items-baseline gap-1 ${className}`}>
          <span>{children}</span>
          {showWatchlist && entity && (
            <WatchlistIndicator
              entityType={entity.entityType}
              entityId={entity.entityId}
            />
          )}
        </span>
      )
    }
    return (
      <EntityProfileLink
        to={`/company/${participant.cvrNummer}`}
        entityType="company"
        entityId={String(participant.cvrNummer)}
        showWatchlist={showWatchlist}
        className={`${className} hover:underline decoration-signal underline-offset-2`}
      >
        {children}
      </EntityProfileLink>
    )
  }
  return (
    <EntityProfileLink
      to={`/person/${participant.enhedsNummer}`}
      entityType="person"
      entityId={String(participant.enhedsNummer)}
      showWatchlist={showWatchlist}
      className={`${className} hover:underline decoration-signal underline-offset-2`}
    >
      {children}
    </EntityProfileLink>
  )
}

function formatStripPercent(
  participant: CVRParticipant,
): { compact: string; full: string } | null {
  const isLegalRegister = participant.role.organizationType === 'EJERREGISTER'
  return formatOwnershipStakeLabels(
    participant.role.ownershipCapitalPercent,
    participant.role.ownershipCapitalPercentMax,
    isLegalRegister ? participant.role.ownershipRegistrationUnderFive : false,
  )
}

const OwnerStakes: React.FC<{ participant: CVRParticipant }> = ({ participant }) => {
  const isLegalRegister = participant.role.organizationType === 'EJERREGISTER'
  const underFive = participant.role.ownershipRegistrationUnderFive
  const capital = isLegalRegister
    ? formatLegalRegisterPercent(
        participant.role.ownershipCapitalPercent,
        participant.role.ownershipCapitalPercentMax,
        underFive
      )
    : formatPercentRange(
        participant.role.ownershipCapitalPercent,
        participant.role.ownershipCapitalPercentMax
      )
  const voting = isLegalRegister
    ? formatLegalRegisterPercent(
        participant.role.ownershipVotingPercent,
        participant.role.ownershipVotingPercentMax,
        underFive
      )
    : formatPercentRange(
        participant.role.ownershipVotingPercent,
        participant.role.ownershipVotingPercentMax
      )
  const from = formatDate(participant.role.validFrom)
  const to = formatDate(participant.role.validTo)

  return (
    <div className="text-right text-sm tabular-nums">
      {capital && (
        <div className="font-semibold text-ink">
          {capital}
          <span className="text-ink-500 font-normal text-xs ml-1">kapital</span>
        </div>
      )}
      {voting && voting !== capital && (
        <div className="text-ink-700">
          {voting}
          <span className="text-ink-500 font-normal text-xs ml-1">stemmer</span>
        </div>
      )}
      <div className="text-xs text-ink-500 mt-0.5 tabular-nums">
        {from && <>Fra {from}</>}
        {from && to && ' '}
        {to && <>til {to}</>}
      </div>
    </div>
  )
}

const RolePeriod: React.FC<{ participant: CVRParticipant }> = ({ participant }) => {
  const from = formatDate(participant.role.validFrom)
  const to = formatDate(participant.role.validTo)
  if (!from && !to) return null
  return (
    <div className="text-right text-xs text-ink-500 tabular-nums">
      {from && <>Fra {from}</>}
      {from && to && <br />}
      {to && <>til {to}</>}
    </div>
  )
}

export default ParticipantRow
